import {
  changeUser,
  initialize,
  logCustomEvent,
  openSession,
  subscribeToContentCardsUpdates,
} from "@braze/web-sdk";

import {
  fetchGeolocation,
  IContentCards,
  IContentCardsResult,
  writeGeolocation,
} from "@origin-digital/event-dispatcher";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { logger } from "@origin-digital/reporting-client";

import { IBrazeUser } from "./consts/interfaces";
import { backroundRefresh, fetchContentCards } from "./contentCard/card";
import { inAppMessageInit } from "./inAppMessages";
import { sendBrazeEvents, fetchMarketingId } from "./utils/";
import {
  BRAZE_ID_KEY,
  getApiKey,
  INAPP_ZINDEX,
  SDK_BASE_URL,
} from "./utils/config";
import { showCPFeatureFlag } from "./featureFlag";

export interface IBrazeProps {
  environment: EnvironmentNames;
  /**
   * pathname from window.location called in setup
   */
  path: string;
  /**
   * current query string as a parameters object
   */
  parameters?: Record<string, string>;
}

let onBrazeInit: Promise<boolean> | undefined;

export const onBrazeInitFn = ({
  environment,
  path,
  parameters,
}: IBrazeProps) => {
  onBrazeInit = new Promise<boolean>((resolve) => {
    fetchMarketingId(environment, parameters?.[BRAZE_ID_KEY] ?? "")
      .then(async (brazeObj: IBrazeUser) => {
        // initialize/set user
        initialize(getApiKey(environment), {
          baseUrl: SDK_BASE_URL,
          enableLogging: environment !== "prod",
          doNotLoadFontAwesome: true,
          inAppMessageZIndex: INAPP_ZINDEX,
        });

        // In-App Messages
        inAppMessageInit(environment, path);

        // DO NOT REMOVE
        // this callback is required for cleaning up cached cards
        subscribeToContentCardsUpdates(({ cards }) => {
          logger.debug("[braze] subscribeToContentCardsUpdates", cards);
        });

        if (brazeObj.bzid == null) {
          resolve(false);
          return;
        }

        // TEMP: quick test of FeatureFlag
        if (window.location.href.indexOf("electricity-gas/plans.html") > -1) {
          showCPFeatureFlag();
        }

        changeUser(brazeObj.bzid);
        openSession();
        fetchGeolocation({})?.then((geolocation) =>
          writeGeolocation(geolocation)
        );

        // fire events/view card
        sendBrazeEvents(logCustomEvent);

        resolve(true);
        backroundRefresh();
      })
      .catch(() => {
        resolve(false);
      });
  });
};

let initUser: boolean | null = null;
export const contentCard = (
  payload: IContentCards["payload"],
  useCached?: boolean
): Promise<IContentCardsResult> => {
  if (initUser === true) {
    return fetchContentCards(payload, useCached);
  } else if (initUser === false) {
    return Promise.resolve({ cards: [], unknownUser: true });
  } else {
    // create promise for after Braze has initalized
    const onInitPromise = new Promise<IContentCardsResult>((resolve) => {
      if (onBrazeInit == null)
        throw Error("[braze]: onBrazeInit must be initalised");
      onBrazeInit.then((success) => {
        initUser = success;
        return resolve(contentCard(payload, useCached));
      });
    });
    return onInitPromise;
  }
};

// export for testing
export const contentCardTimeout = ({
  payload,
}: {
  payload: IContentCards["payload"];
}): IContentCards["result"] => {
  const EXISTING_USERS_DEFAULT_TIMEOUT_30s = 1000 * 30;
  const contentCardTimeOutFn = (timeout: number) =>
    new Promise<IContentCardsResult>((resolve) => {
      setTimeout(
        () =>
          resolve(
            initUser === null
              ? { cards: [], timeout: true } // if we aren't initalized return empty array[]
              : contentCard(payload, true).then((result) => ({
                  ...result,
                  timeout: true,
                }))
          ),
        payload.timeout ?? timeout
      );
    });
  return Promise.race<IContentCardsResult>([
    contentCard(payload),
    contentCardTimeOutFn(EXISTING_USERS_DEFAULT_TIMEOUT_30s),
  ]);
};

// export for testing
export const __resetForTesting = () => {
  onBrazeInit = undefined;
  initUser = null;
};
